import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Calendar } from "primereact/calendar";

import CandidateUpdate from "./CandidateUpdate";
import { ICandidate, ICandidateListProps } from "./Candidate.model";
import { candidateService } from "../../service/Candidate/candidate.service";
import { ConfirmDialog } from "primereact/confirmdialog";
import GenerateExamLink from "../CandidateExam/GenerateExamLink";
import ExamAttempts from "../ExamAttempts";

import {
  PERMISSIONS_ACTION,
  PERMISSIONS_MODEL,
} from "../../library/utilities/constants/permissions";
import { Can } from "../../ability/can";
import { useContext } from "react";
import { MessageContext } from "../../context/MessageContext";
import { Nullable } from "primereact/ts-helpers";

const CandidateList = ({
  openNew,
  loadCandidates,
  data,
  loading,
}: ICandidateListProps) => {
  const [candidateDialog, setCandidateDialog] = useState(false);
  const [selectedCandidates, setSelectedCandidates] =
    useState<ICandidate | null>(null);
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [candidate, setCandidate] = useState<ICandidate | null>(null);
  const [openExamAttemptsDialog, setOpenExamAttemptsDialog] = useState(false);
  const { pushMessageForToast } = useContext(MessageContext);
  const [id, setId] = useState<number>(0);
  const deleteCandidate = (id: number) => {
    setId(id);
    setConfirmDialog(true);
  };
  const [globalFilter, setGlobalFilter] = useState("");
  const [dateRange, setDateRange] = useState<Nullable<(Date | null)[]>>(null);
  const confirmedDeleteCandidate = async () => {
    const response = await candidateService.remove(id);
    if (!response.success) {
      pushMessageForToast({
        severity: "error",
        message: response?.errors?.message,
      });
    }
    loadCandidates();
  };

  const actionBodyTemplate = (row: ICandidate) => {
    return (
      <div className="flex gap-2">
        <Can
          I={PERMISSIONS_ACTION.CANDIDATE_CHANGE}
          a={PERMISSIONS_MODEL.CANDIDATE}>
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => {
              setCandidate(row);
              setCandidateDialog(true);
            }}
          />
        </Can>

        <Can
          I={PERMISSIONS_ACTION.CANDIDATE_DELETE}
          a={PERMISSIONS_MODEL.CANDIDATE}>
          <Button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            onClick={() => deleteCandidate(row.id)}
          />
        </Can>

        <Can
          I={PERMISSIONS_ACTION.CANDIDATE_GENERATE_EXAM_LINK}
          a={PERMISSIONS_MODEL.CANDIDATE}>
          <GenerateExamLink candidate={row} loadCandidates={loadCandidates} />
        </Can>
      </div>
    );
  };

  const columns = [
    { field: "name", header: "Name" },
    { field: "email", header: "Email" },
    { field: "gender", header: "Gender" },
    { field: "phone", header: "Phone" },
    { field: "hobby", header: "Hobby" },
    {
      field: "exam_attempts",
      header: "Exam Attempts",
      body: (row: ICandidate) => (
        <span
          className="text-primary underline cursor-pointer font-bold"
          onClick={() => setOpenExamAttemptsDialog(true)}>
          {row.exam_attempts}
        </span>
      ),
    },
    { field: "year_of_experience", header: "Years of Experience" },
  ];

  // LIFO (Last In, First Out): sort candidates by id descending so the most recently added appears first
  const sortedData = [...data]?.sort((a, b) => b.id - a.id);

  const filteredData = sortedData.filter((candidate: ICandidate) => {
    const search = globalFilter.toLowerCase();
    const searchMatch =
      candidate.name.toLowerCase().includes(search) ||
      candidate.email.toLowerCase().includes(search);

    // Date range filter
    if (dateRange && dateRange.length === 2) {
      const [startDate, endDate] = dateRange;
      if (startDate || endDate) {
        // Check if candidate has created_date
        if (!(candidate as any).created_date) {
          return searchMatch;
        }

        const candidateDate = new Date((candidate as any).created_date);

        // Check if candidateDate is valid
        if (isNaN(candidateDate.getTime())) {
          return searchMatch;
        }

        if (startDate && endDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          const dateMatch = candidateDate >= start && candidateDate <= end;
          return searchMatch && dateMatch;
        } else if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          const dateMatch = candidateDate >= start;
          return searchMatch && dateMatch;
        } else if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          const dateMatch = candidateDate <= end;
          return searchMatch && dateMatch;
        }
      }
    }

    return searchMatch;
  });

  return (
    <>
      <Dialog
        visible={candidateDialog}
        header="Update Candidate"
        modal
        onHide={() => setCandidateDialog(false)}
        className="w-4">
        {candidate && (
          <CandidateUpdate
            data={candidate}
            setCandidateDialog={setCandidateDialog}
            loadCandidates={loadCandidates}
          />
        )}
      </Dialog>

      <DataTable
        value={filteredData}
        loading={loading}
        selection={selectedCandidates}
        onSelectionChange={(e) => setSelectedCandidates(e.value as ICandidate)}
        dataKey="id"
        paginator
        rows={10}
        header={
          <div className="flex flex-column gap-3">
            <div className="flex justify-content-between align-items-center">
              <h3>Candidate Management</h3>
              <Can
                I={PERMISSIONS_ACTION.CANDIDATE_ADD}
                a={PERMISSIONS_MODEL.CANDIDATE}>
                <Button label="New Candidate" onClick={openNew} />
              </Can>
            </div>
            <div className="flex gap-3 align-items-center justify-content-end">
              <span className="p-input-icon-left">
                <i className="pi pi-search" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by name, email"
                  className="p-inputtext p-component"
                />
              </span>
              <div className="flex gap-2 align-items-center">
                <label className="text-sm font-medium">Date Range:</label>
                <Calendar
                  value={dateRange}
                  onChange={(e) => {
                    setDateRange(e.value as Date[] | null);
                  }}
                  selectionMode="range"
                  placeholder="Select date range"
                  dateFormat="yy-mm-dd"
                  showIcon
                  showButtonBar
                  readOnlyInput
                />
              </div>
            </div>
          </div>
        }
        selectionMode="single">
        {columns.map((col) => (
          <Column
            sortable
            key={col.field}
            field={col.field}
            header={col.header}
            body={col?.body}
          />
        ))}
        <Column body={actionBodyTemplate} header="Actions"></Column>
      </DataTable>

      <ConfirmDialog
        visible={confirmDialog}
        className="w-4"
        onHide={() => setConfirmDialog(false)}
        message={`Are you sure you want to delete this candidate?
           Because when you delete this candidate their all exam attempts will be deleted also.`}
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmedDeleteCandidate}
        reject={() => setConfirmDialog(false)}
      />
      <Dialog
        visible={openExamAttemptsDialog}
        header="Exam Attempts"
        maximized={true}
        modal
        appendTo="self"
        maximizable
        onHide={() => setOpenExamAttemptsDialog(false)}
        className="w-4">
        <ExamAttempts selectedCandidates={selectedCandidates} />
      </Dialog>
    </>
  );
};

export default CandidateList;
